import { env } from '@/utils/env'

/**
 * Deep link scheme registered by the desktop app (tauri-plugin-deep-link).
 * The desktop app parses scrambl:///acme/tasks?task=123 into /acme/tasks?task=123
 * via parseDeepLinkNavigationPath in lib/utils/tauri.ts.
 */
const DESKTOP_DEEP_LINK_SCHEME = 'scrambl'

/**
 * Every scheme a desktop build may register: production, the dev build and the
 * local build. Used to validate a scheme handed to us before it is put in a URL.
 */
export const DESKTOP_DEEP_LINK_SCHEMES = [
  'scrambl',
  'scrambl-dev',
  'scrambl-local',
]

const webAppBaseUrl = env.appUrl.replace(/\/+$/, '')

function normalizePath(path: string): string {
  return path.startsWith('/') ? path : `/${path}`
}

/**
 * Build a shareable web URL for an in-app path (https://app.scrambl.com/... in
 * production). Defaults to the current page. Used instead of
 * window.location.origin so links copied from the desktop app point at the
 * hosted web app rather than tauri://localhost.
 */
export function getShareableWebUrl(
  path: string = window.location.pathname + window.location.search,
): string {
  return `${webAppBaseUrl}${normalizePath(path)}`
}

/**
 * Build a scrambl:// deep link that opens the desktop app at an in-app path.
 *
 * Dev and local desktop builds register their own schemes, so a link meant for
 * a specific running build must pass the scheme it reported via
 * getDesktopDeepLinkScheme(). Anything unrecognised falls back to production.
 */
export function getDesktopDeepLinkUrl(path: string, scheme?: string): string {
  const resolved =
    scheme && DESKTOP_DEEP_LINK_SCHEMES.includes(scheme)
      ? scheme
      : DESKTOP_DEEP_LINK_SCHEME
  return `${resolved}://${normalizePath(path)}`
}

/**
 * Try to hand the current page off to the desktop app via its deep link
 * scheme. Browsers offer no API to check whether a protocol handler is
 * installed, so this uses the standard heuristic: if the browser loses focus
 * shortly after triggering the link, the OS handed it to the app.
 *
 * Resolves true when the app appears to have opened, false when nothing
 * handled the scheme within the timeout (likely not installed).
 */
export function attemptOpenDesktopApp(
  path: string,
  timeoutMs = 2500,
): Promise<boolean> {
  return new Promise(resolve => {
    let settled = false

    const settle = (didOpen: boolean) => {
      if (settled) return
      settled = true
      window.removeEventListener('blur', handleBlur)
      document.removeEventListener('visibilitychange', handleVisibilityChange)
      clearTimeout(timer)
      resolve(didOpen)
    }

    const handleBlur = () => settle(true)
    const handleVisibilityChange = () => {
      if (document.hidden) settle(true)
    }

    window.addEventListener('blur', handleBlur)
    document.addEventListener('visibilitychange', handleVisibilityChange)
    const timer = setTimeout(() => settle(false), timeoutMs)

    window.location.href = getDesktopDeepLinkUrl(path)
  })
}
